import {Component, EventEmitter, Input, OnInit, Output, computed, inject, Signal} from '@angular/core';
import {AccordionModule} from "@syncfusion/ej2-angular-navigations";
import {AvatarComponent} from "../../../shared/elements/avatar/avatar.component";
import {DialogModule} from "@syncfusion/ej2-angular-popups";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {DialogService} from "../../../shared/services/dialog.service";
import {take} from 'rxjs';
import {ApiService} from "../../../shared/services/api.service";
import {ProfileService} from "../../../shared/services/profile.service";
import {ContentComponent} from "../../../shared/elements/content/content.component";
import {CapabilitiesService} from '../../../shared/services/capabilities.service';
import {ChatStore} from '../../chat/chat-store/chat-store';
import {LanguageService} from '../../../shared/services/language.service';
import {ToasterService} from '../../../shared/services/toaster.service';
import {RouterService} from '../../../shared/services/router.service';
import {NavService} from '../../../shared/services/nav.service';
import {SettingsMenuItemType} from '../../../shared/types/generated/settingsMenuItemType';
import {LayoutService} from '../../../shared/services/layout.service';
import {UserContactResource} from '../../../shared/types/generated/userContactResource';
import {GroupMinimalResource} from '../../../shared/types/generated/groupMinimalResource';
import {isContactActive} from '../../../shared/utils/contact-status';

@Component({
    selector: 'app-dialog-profile-avatar',
    imports: [
        AccordionModule,
        AvatarComponent,
        DialogModule,
        FormButtonComponent,
        HeadlineComponent,
        TranslatePipe,
        ContentComponent,
    ],
    templateUrl: './dialog-profile-avatar.component.html',
    styleUrl: './dialog-profile-avatar.component.scss'
})
export class DialogProfileAvatarComponent implements OnInit {
  public readonly isContactActive: typeof isContactActive = isContactActive;
  public dialogService: DialogService = inject(DialogService);
  public layoutService: LayoutService = inject(LayoutService);
  private apiService: ApiService = inject(ApiService);
  private profileService: ProfileService = inject(ProfileService);
  private capabilitiesService: CapabilitiesService = inject(CapabilitiesService);
  private chatStore: InstanceType<typeof ChatStore> = inject(ChatStore);
  private languageService: LanguageService = inject(LanguageService);
  private toasterService: ToasterService = inject(ToasterService);
  private routerService: RouterService = inject(RouterService);
  private navService: NavService = inject(NavService);

  @Input() public visible: boolean = false;
  @Output() public  visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public profile: UserContactResource | null = null;
  public canCreateChat: Signal<boolean> = computed(() => !!this.capabilitiesService.capabilities().chat?.create);

  public ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.apiService.getContact(this.profileService.profileAvatarUserID())
      .pipe(take(1))
      .subscribe((profile: UserContactResource) => {
        this.profile = profile;
      });
  }
  public close(): void {
    this.visibleChange.emit(false);
  }

  public profileImage(): string {
    return this.profile?.avatar?.url?.responsive ?? '';
  }

  public groupImage(group: GroupMinimalResource): string {
    return group.avatar?.url?.responsive ?? '';
  }

  public open(url: string): void {
    const timeoutDuration = 2000;
    let hasBlurred = false;

    const onBlur = () => {
      hasBlurred = true;
    };

    window.addEventListener('blur', onBlur);
    this.routerService.openURL(url, '_self');

    setTimeout(() => {
      window.removeEventListener('blur', onBlur);
      if (!hasBlurred) {
        const title = url.includes('mailto:')
          ? this.languageService.getTranslationByKey('OPENEMAILWARNINGTITLE')
          : this.languageService.getTranslationByKey('OPENTELWARNINGTITLE');
        const message = url.includes('mailto:')
          ? this.languageService.getTranslationByKey('OPENEMAILWARNINGMESSAGE')
          : this.languageService.getTranslationByKey('OPENTELWARNINGMESSAGE');
        this.toasterService.warning(title, message);
      }
    }, timeoutDuration);
  }

  public startChat(): void {
    if (this.profile?.id && this.isAllowedToChat()) {
      this.chatStore.startChatWithParticipant(this.profile.id);
      this.close();
    }
  }

  public isAllowedToChat(): boolean {
    const user = this.profileService.profile()?.user.id ?? null;
    if (user === null || this.profile?.id === user) {
      return false;
    }
    if (this.profile && !isContactActive(this.profile.active)) {
      return false;
    }
    return this.navService.userMenu().some(menuItem => menuItem.type === SettingsMenuItemType.CHAT);
  }
}
